/**
 * Get the full URL for an avatar or logo.
 * - Passes through http:// URLs and data: URIs unchanged.
 * - Prefixes relative paths with the API base URL.
 * @param {string} path - The image path or base64 data URI
 * @returns {string|null}
 */
export const getAvatarUrl = (path) => {
    if (!path) return null;

    // Handle data URIs: sanitize and check for truncation
    if (path.startsWith('data:')) {
        const sanitized = path.trim().replace(/\s/g, '');
        if (sanitized.length <= 255) {
            return null;
        }
        return sanitized;
    }

    if (path.startsWith('http')) return path;

    // Prefix relative paths with API base URL
    // If VITE_API_URL is undefined (like on Vercel), fallback to an empty string to use relative path
    const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || '';
    return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

/**
 * Handle image loading errors.
 * Uses a data-attribute on the img element to track retry state — never appends to the src,
 * because that corrupts data: URIs (base64 strings don't support URL query parameters).
 * @param {Event} e - The error event from the <img> tag
 */
export const handleAvatarError = (e) => {
    const img = e.target;
    const currentSrc = img.getAttribute('src') || '';

    // -- Retry: if PNG base64 fails, try JPEG prefix (Odoo sometimes serves JPEG with PNG header)
    if (currentSrc.startsWith('data:image/png;base64,') && !img.dataset.retriedJpeg) {
        console.warn('Avatar: PNG data URI failed, retrying as JPEG');
        img.dataset.retriedJpeg = '1';
        img.src = currentSrc.replace('data:image/png;base64,', 'data:image/jpeg;base64,');
        return;
    }

    // -- Retry: if JPEG base64 fails, try PNG prefix
    if (currentSrc.startsWith('data:image/jpeg;base64,') && !img.dataset.retriedPng) {
        console.warn('Avatar: JPEG data URI failed, retrying as PNG');
        img.dataset.retriedPng = '1';
        img.src = currentSrc.replace('data:image/jpeg;base64,', 'data:image/png;base64,');
        return;
    }

    // -- Both formats tried (or non-base64 URL failed) → hide image so initials show through
    img.style.display = 'none';
};
